/**
 * 내부 날짜 파싱 — 4포맷(compact 8자리 / - . / 구분) + 한국식(목표 동작, value-spec 05 §3)
 * + 달력 검증(윤년 포함). 기존 `ixd-editor/value-validation.ts` parseDateValue 복제에
 * 한국식 포맷을 추가한 것.
 */

const COMPACT_DATE_VALUE_PATTERN = /^\d{8}$/;
// YYYY[sep]M[sep]D — 구분자는 - . /, 앞뒤 공백 허용, 월/일은 1~2자리
// (엑셀·DSD 복붙: "2023-11-27", "2023.11.27", "2023/11/27", "2023. 1. 5")
const SEPARATED_DATE_VALUE_PATTERN =
  /^(\d{4})\s*[-./]\s*(\d{1,2})\s*[-./]\s*(\d{1,2})$/;
// 한국식 "2023년 11월 27일" — 현행 편집기는 REJECT, spec 목표 동작으로 지원
const KOREAN_DATE_VALUE_PATTERN =
  /^(\d{4})\s*년\s*(\d{1,2})\s*월\s*(\d{1,2})\s*일$/;

const isLeapYear = (year: number): boolean =>
  year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);

const getDaysInMonth = (year: number, month: number): number => {
  switch (month) {
    case 2:
      return isLeapYear(year) ? 29 : 28;
    case 4:
    case 6:
    case 9:
    case 11:
      return 30;
    default:
      return 31;
  }
};

export function parseDateValue(value: string): {
  compactValue: string;
  dashedValue: string;
} | null {
  const trimmed = value.trim();

  if (trimmed === "") return null;

  let compactValue: string;
  if (COMPACT_DATE_VALUE_PATTERN.test(trimmed)) {
    compactValue = trimmed;
  } else {
    const separated =
      trimmed.match(SEPARATED_DATE_VALUE_PATTERN) ??
      trimmed.match(KOREAN_DATE_VALUE_PATTERN);
    if (!separated) return null;
    compactValue =
      separated[1] +
      separated[2].padStart(2, "0") +
      separated[3].padStart(2, "0");
  }

  const year = Number(compactValue.slice(0, 4));
  const month = Number(compactValue.slice(4, 6));
  const day = Number(compactValue.slice(6, 8));

  if (!Number.isInteger(year) || year < 1) return null;
  if (!Number.isInteger(month) || month < 1 || month > 12) return null;
  if (!Number.isInteger(day) || day < 1 || day > getDaysInMonth(year, month)) {
    return null;
  }

  return {
    compactValue,
    dashedValue: `${compactValue.slice(0, 4)}-${compactValue.slice(4, 6)}-${compactValue.slice(6, 8)}`,
  };
}
